/* CreateEditRedactionRenderer.js */

"use strict";
const _ = require("lodash");
const uiCore = require("@gluenetworks/steve_ui_core");
const TextControl = uiCore.Control.Textfield.InputText;
const ButtonControl = uiCore.Control.Action.Button;
const ComboBoxControl = uiCore.Control.OptionSelection.Single.ComboBox;
const CheckboxControl = uiCore.Control.Boolean.InputCheckbox;
const TextAreaControl = uiCore.Control.Textfield.Textarea;
const MODAL_TITLE = "Add / Edit Redaction Expression";
class CreateEditRedactionRenderer {
    constructor(model) {
        /**
         * @type {CreateEditRedactionRenderer}
         */
        this._model = model;
        this._rootElem = this._createRootElement();
        this._name = new TextControl({
            darkTheme: true,
            readOnly: false,
        });
        this._findString = new TextControl({
            darkTheme: true,
            readOnly: false,
        });
        this._redactionString = new TextControl({
            darkTheme: true,
            readOnly: false,
        });
        this._caseInsensitiveCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Case insensitive",
            title: "Case Insensitive",
            disabled: false,
        });
        this._multilineCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Multiline",
            title: "Multiline",
            disabled: false,
        });
        this._globalCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Global",
            title: "Global",
            disabled: false,
        });
        this._funcCombo = new ComboBoxControl({
            showFilter: false,
            adjustWidth: true,
            darkTheme: true,
        });

        // Controls for Test
        this._testInputString = new TextAreaControl({
            darkTheme: true,
            readOnly: false,
        });
        this._redactedString = new TextAreaControl({
            darkTheme: true,
            readOnly: false,
            disabled: true,
        });
        this._testButton = new ButtonControl({label: "Test", darkTheme: true, disabled: true});

        this._errMsg = document.createElement("error-elem");
        this._saveButton = new ButtonControl({
            label: "Save",
            darkTheme: true,
            disabled: true,
            active: true,
        });
        this._cancelButton = new ButtonControl({id: "cancel", label: "Cancel", disabled: false});
        this._registerListeners();
        this.render();
    }
    get rootElement() {
        return this.getRootElement();
    }
    get model() {
        return this.getModel();
    }
    /*******************************************/
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    render() {
        if (this._renderProm === null) {
            return Promise.resolve();
        }
        this._renderProm = this._renderProm || this._render();
        return this._renderProm;
    }
    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }
    getRootElement() {
        return this._rootElem;
    }
    getModel() {
        return this._model;
    }
    /********************************************/
    /* END PUBLIC COMPONENT RENDERER METHODS    */
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._saveButton.addListener(
            "UserClick",
            (this._saveandclosePopUp = this._saveandclosePopUp.bind(this))
        );
        this._cancelButton.addListener(
            "UserClick",
            (this._closePopUp = this._closePopUp.bind(this))
        );
        this._testButton.addListener("UserClick", (this._testClick = this._testClick.bind(this)));
        this._name.on("UserValue", (this._updateEntity = this._updateEntity.bind(this)));
        this._findString.on("UserValue", (this._updateEntity = this._updateEntity.bind(this)));
        this._redactionString.on("UserValue", (this._updateEntity = this._updateEntity.bind(this)));

        this._testInputString.on(
            "UserValue",
            (this._testInputChange = this._testInputChange.bind(this))
        );

        this._caseInsensitiveCheckbox.on(
            "UserValue",
            (this._updateEntity = this._updateEntity.bind(this))
        );
        this._multilineCheckbox.on(
            "UserValue",
            (this._updateEntity = this._updateEntity.bind(this))
        );
        this._globalCheckbox.on("UserValue", (this._updateEntity = this._updateEntity.bind(this)));
        this._model.on("Error", (this._onError = this._onError.bind(this)));
    }
    _unregisterListeners() {
        this._saveButton.removeListener("UserClick", this._saveandclosePopUp);
        this._cancelButton.removeListener("UserClick", this._closePopUp);

        this._findString.removeListener("UserValue", this._updateEntity);
        this._redactionString.removeListener("UserValue", this._updateEntity);
        this._testButton.removeListener("UserClick", this._testClick);
        this._name.removeListener("UserValue", this._updateEntity);
        this._model.removeListener("Error", this._onError);
        this._testInputString.removeListener("UserValue", this._testInputChange);
    }
    _render() {
        return this._model._whenModelIsReady().then(() => {
            this._rootElem.innerHTML = "";
            const root = document.createDocumentFragment();
            const title = document.createElement("h2");
            title.innerText = MODAL_TITLE;
            root.appendChild(title);
            root.appendChild(this._createSection("Name", this._name));

            const redactionContainer = document.createElement("redaction-form-container");

            this._createForm(redactionContainer);
            redactionContainer.appendChild(this._createTestSection());

            root.appendChild(
                this._createBlockSection(redactionContainer, {
                    bordered: true,
                    title: "Regular Expression",
                })
            );

            this._setFormValues();
            root.appendChild(this._errMsg);
            root.appendChild(this._createActionsContainer());
            return new Promise((resolve /*,reject*/) => {
                this._rootElem.appendChild(root);
                this._enableDisableSaveButton();

                this._name.focus();

                resolve();
            });
        });
    }
    _createForm(elem) {
        const _findStringSec = this._createSection("Find String", this._findString);
        const _redactionStringSec = this._createSection(
            "Replacement String",
            this._redactionString
        );
        elem.appendChild(_findStringSec);
        elem.appendChild(_redactionStringSec);
        const checkboxContainer = document.createElement("div");
        checkboxContainer.appendChild(this._caseInsensitiveCheckbox._renderer._rootElem);
        checkboxContainer.appendChild(this._multilineCheckbox._renderer._rootElem);
        checkboxContainer.appendChild(this._globalCheckbox._renderer._rootElem);
        elem.appendChild(checkboxContainer);
    }
    _setFormValues() {
        this._name.setValue(this._model._redactionEntityInst.getName());
        this._findString.setValue(this._model._redactionEntityInst.getRegex());
        this._redactionString.setValue(this._model._redactionEntityInst.getRedactionString());
        this._caseInsensitiveCheckbox.setValue(
            this._model._redactionEntityInst.getIsCaseInsensitive()
        );
        this._multilineCheckbox.setValue(this._model._redactionEntityInst.getIsMultiline());
        this._globalCheckbox.setValue(this._model._redactionEntityInst.getIsGlobal());
    }
    _shutdown() {
        this._unregisterListeners();
        this._rootElem.remove();
        this._rootElem = null;
        return Promise.all([this._saveButton.shutdown(), this._cancelButton.shutdown()])
            .then(() => {
                this._model = null;
                this._shutdownProm = null;
                this._renderProm = null;
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }
    /********************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS   */
    /* START PRIVATE RENDERER METHODS           */
    /********************************************/
    _createRootElement() {
        const rootElement = document.createElement("div");
        rootElement.classList.add("create-edit-redaction", "modal_feedback_container");
        rootElement.component = this._model;
        return rootElement;
    }
    _createSection(name, Compnent) {
        const section = document.createElement("section");
        const label = document.createElement("label");
        section.className = "key-section";
        label.innerText = name;
        Compnent.setAttribute("rows", "6");
        section.appendChild(label);
        section.appendChild(Compnent.renderer.rootElement);
        return section;
    }
    _createBlockSection(content, options = {}) {
        const section = document.createElement("section");
        section.className = options.className || "";
        section.classList.toggle("bordered", !!options.bordered);
        if (options.title) {
            const title = document.createElement("h2");
            title.innerText = options.title;
            section.appendChild(title);
        }
        section.appendChild(content);
        return section;
    }
    _createActionsContainer() {
        const container = document.createElement("section");
        container.className = "actions-container";
        container.appendChild(this._cancelButton.renderer.rootElement);
        container.appendChild(this._saveButton.renderer.rootElement);
        return container;
    }

    _createTestSection() {
        const testArea = document.createElement("div");
        testArea.classList.add("test-container");
        const testStringLabel = document.createElement("label");
        testStringLabel.innerText = "Original String";
        const redactedStringLabel = document.createElement("label");
        redactedStringLabel.innerText = "Redacted String";
        const origStringArea = document.createElement("div");
        origStringArea.classList.add("text-area");
        const redacStringArea = document.createElement("div");
        redacStringArea.classList.add("text-area");

        origStringArea.appendChild(testStringLabel);
        origStringArea.appendChild(this._testInputString._renderer._rootElem);
        redacStringArea.appendChild(redactedStringLabel);
        redacStringArea.appendChild(this._redactedString._renderer._rootElem);
        testArea.appendChild(origStringArea);
        testArea.appendChild(redacStringArea);
        testArea.appendChild(this._testButton._renderer._rootElem);
        return this._createBlockSection(testArea, {title: "Test Expression", bordered: true});
    }
    /********************************************/
    /* END PRIVATE RENDERER METHODS             */
    /* START HANDLER METHODS                    */
    /********************************************/
    /**/
    _saveandclosePopUp() {
        return this._model.saveandclosePopUp();
    }
    _closePopUp() {
        // this._model
        return this._model.closePopUp();
    }
    _testClick() {
        const findString = _.trim(this._findString.getValue()),
            redactionString = _.trim(this._redactionString.getValue()),
            originalString = _.trim(this._testInputString.getValue()),
            cI = this._caseInsensitiveCheckbox.getValue() ? "i" : "",
            mL = this._multilineCheckbox.getValue() ? "m" : "",
            gL = this._globalCheckbox.getValue() ? "g" : "",
            regEx = new RegExp(findString, `${cI}${mL}${gL}`);

        const redactedString = originalString.replace(regEx, redactionString);

        this._redactedString.setValue(redactedString);
    }
    _testInputChange() {
        this._redactedString.setValue("");
        if (_.trim(this._testInputString.getValue()) !== "") {
            this._testButton.setDisabled(false);
        } else {
            this._testButton.setDisabled(true);
        }
    }
    _updateEntity() {
        this._redactedString.setValue("");
        this._model._redactionEntityInst.setName(this._name.getValue() || "");
        this._model._redactionEntityInst.setRegex(this._findString.getValue() || "");
        this._model._redactionEntityInst.setRedactionString(this._redactionString.getValue() || "");
        this._model._redactionEntityInst.setIsCaseInsensitive(
            this._caseInsensitiveCheckbox.getValue()
        );
        this._model._redactionEntityInst.setIsMultiline(this._multilineCheckbox.getValue());
        this._model._redactionEntityInst.setIsGlobal(this._globalCheckbox.getValue());
        this._enableDisableSaveButton();
        this._clearError();
    }
    _enableDisableSaveButton() {
        if (!this._formInvalid()) {
            if (!_.isEmpty(this._model._redactionEntityInst._pendingPatches)) {
                this._saveButton.setDisabled(false);
            } else {
                this._saveButton.setDisabled(true);
            }
        } else {
            this._saveButton.setDisabled(true);
        }
    }
    _formInvalid() {
        return _.isEmpty(this._name.getValue()) || _.isEmpty(this._findString.getValue());
    }
    _onError(err) {
        this._errMsg.innerText = err;
    }
    _clearError() {
        this._errMsg.innerText = "";
    }
    /********************************************/
    /* END HANDLER METHODS                      */
    /* START PRIVATE API METHODS                */
    /********************************************/
    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /* START PUBLIC API METHODS                */
    /********************************************/
    /*******************************************/
    /*  END PUBLIC API METHODS                */
    /*******************************************/
}
module.exports = CreateEditRedactionRenderer;
